import React, { useState, useEffect, useRef } from 'react';
import { Html5QrcodeScanner, Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import "../assets/CSS/Dashboard.css";
import axiosInstance from '../axiosConfig';
import toast from 'react-hot-toast';

function BarcodeScanner() {
  const [isSidebarVisible, setIsSidebarVisible] = useState(window.innerWidth >= 768);
  const [barcode, setBarcode] = useState('');
  const [scannedItem, setScannedItem] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [isChecking, setIsChecking] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isScanningCamera, setIsScanningCamera] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    let scanner = null;
    if (isScanningCamera) {
      scanner = new Html5QrcodeScanner("reader", { 
        fps: 10, 
        qrbox: { width: 250, height: 250 },
        formatsToSupport: [
          Html5QrcodeSupportedFormats.CODE_128,
          Html5QrcodeSupportedFormats.CODE_39,
          Html5QrcodeSupportedFormats.QR_CODE
        ]
      }, false);
      scanner.render(
        (decodedText) => {
          setBarcode(decodedText);
          setIsScanningCamera(false);
          scanner.clear();
        },
        (error) => {
          // ignore scan errors
        }
      );
    }
    return () => {
      if (scanner) {
        scanner.clear().catch(error => {
          console.error("Failed to clear html5QrcodeScanner. ", error);
        });
      }
    };
  }, [isScanningCamera]);

  const handleImageScanClick = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleImageScanFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    toast.loading("Scanning image for barcode...", { id: "scanToast" });

    const imgURL = URL.createObjectURL(file);
    const img = new Image();
    img.src = imgURL;

    img.onload = async () => {
      let barcodeFound = null;

      // 1. Try Native BarcodeDetector API (Highly accurate on Chrome/Android)
      if ('BarcodeDetector' in window) {
        try {
          // eslint-disable-next-line no-undef
          const barcodeDetector = new BarcodeDetector();
          const barcodes = await barcodeDetector.detect(img);
          if (barcodes && barcodes.length > 0) {
            barcodeFound = barcodes[0].rawValue;
          }
        } catch (err) {
          console.warn("BarcodeDetector API failed", err);
        }
      }

      // 2. Fallback to html5-qrcode
      if (!barcodeFound) {
        try {
          const html5QrCode = new Html5Qrcode("reader-hidden");
          barcodeFound = await html5QrCode.scanFile(file, false);
        } catch (err) {
          console.warn("html5-qrcode failed", err);
        }
      }

      URL.revokeObjectURL(imgURL);

      if (barcodeFound) {
        setBarcode(barcodeFound);
        toast.success("Barcode scanned successfully!", { id: "scanToast" });
      } else {
        toast.error("Could not find barcode. Please crop the image, improve lighting, or type manually.", { id: "scanToast" });
      }
    };

    e.target.value = '';
  };

  const toggleSidebar = () => setIsSidebarVisible(!isSidebarVisible);

  const handleBarcodeChange = (e) => {
    setBarcode(e.target.value);
  };

  const checkBarcode = async () => {
    if (!barcode) {
      toast.error('Please enter a barcode');
      return;
    }

    setIsChecking(true);
    setScannedItem(null);

    try {
      const response = await axiosInstance.get(`/api/wip/check-stock/${barcode}`);
      if (response.data && response.data.data) {
        setScannedItem(response.data.data);
        toast.success('Barcode found in inventory!');
      }
    } catch (error) {
      console.error(error);
      toast.error('Barcode not found in StockAudit!');
    } finally {
      setIsChecking(false);
    }
  };

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setImageFile(e.target.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!scannedItem) {
      toast.error('Please verify barcode first.');
      return;
    }
    if (!imageFile) {
      toast.error('Please select an image to upload.');
      return;
    }

    setIsUploading(true);
    const formData = new FormData();
    formData.append('barcode', barcode);
    formData.append('productName', scannedItem.itemName);
    formData.append('image', imageFile);

    try {
      await axiosInstance.post('/api/wip/upload-raw-material', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      toast.success('Uploaded successfully!');
      
      // Reset form
      setBarcode('');
      setScannedItem(null);
      setImageFile(null);
      document.getElementById('image-upload').value = '';
      
    } catch (error) {
      console.error(error);
      toast.error('Failed to upload data.');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="d-flex dashboard">
      <div className={isSidebarVisible ? "sidebar-container shadow-sm" : "sidebar-hidden"}>
        <Sidebar />
      </div>

      <div className="flex-grow-1">
        <Header toggleSidebar={toggleSidebar} isSidebarVisible={isSidebarVisible} />

        <main className="main-container p-0 pb-5" style={{ background: "#f8fafc", fontFamily: "'Inter', sans-serif", minHeight: "calc(100vh - 60px)" }}>
          {/* PAGE BANNER */}
          <div style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)", padding: "40px 48px", color: "#fff", position: "relative", overflow: "hidden" }}>
            <div style={{ position: "absolute", top: -50, right: -50, width: 200, height: 200, background: "linear-gradient(135deg, rgba(59,130,246,0.3) 0%, rgba(239,68,68,0.2) 100%)", borderRadius: "50%", filter: "blur(40px)" }}></div>
            <h1 style={{ margin: 0, fontSize: 32, fontWeight: 800, letterSpacing: "-0.5px", display: "flex", alignItems: "center" }}>
              <div style={{ background: "rgba(59, 130, 246, 0.2)", padding: "10px", borderRadius: "12px", marginRight: "16px" }}>
                <i className="bi bi-upc-scan text-primary" style={{ fontSize: "24px" }}></i>
              </div>
              Barcode Scanner
            </h1>
            <p style={{ margin: "12px 0 0 64px", opacity: 0.8, fontSize: 16, fontWeight: 500, maxWidth: "600px" }}>
              Scan raw material barcodes and upload related images directly into the inventory system.
            </p>
          </div>

          <div className="container-fluid mt-4" style={{ maxWidth: "1200px", padding: "0 40px" }}>
            <div className="card shadow-sm border-0 mb-4 mx-auto" style={{ borderRadius: "20px", overflow: "hidden", maxWidth: "600px" }}>
              <div className="card-body p-4 p-md-5">
                <label style={{ fontWeight: 700, color: "#475569", fontSize: "15px", marginBottom: "12px", display: "block" }}>Scan or Enter Lot Barcode</label>
                
                <div className="d-flex gap-3 mb-4">
                  <input 
                    type="text" 
                    placeholder="e.g. RY235JULY..." 
                    style={{ flex: 1, borderRadius: "12px", border: "2px solid #8b5cf6", padding: "12px 16px", outline: "none", color: "#334155", fontWeight: 600, fontSize: "16px", minWidth: 0 }}
                    value={barcode}
                    onChange={handleBarcodeChange}
                  />
                  <button 
                    onClick={checkBarcode}
                    disabled={isChecking}
                    style={{ background: "#6366f1", color: "#fff", border: "none", borderRadius: "12px", padding: "0 24px", fontWeight: 700, fontSize: "16px", minWidth: "90px" }}
                  >
                    {isChecking ? "..." : "Find"}
                  </button>
                </div>

                <div className="d-flex align-items-center mb-4">
                  <hr className="flex-grow-1 m-0" style={{ borderColor: "#e2e8f0" }} />
                  <span className="px-3" style={{ fontSize: "13px", fontWeight: 700, color: "#94a3b8" }}>OR</span>
                  <hr className="flex-grow-1 m-0" style={{ borderColor: "#e2e8f0" }} />
                </div>

                {isScanningCamera && (
                  <div id="reader" className="mb-4" style={{ width: "100%", borderRadius: "12px", overflow: "hidden", border: "2px solid #6366f1", backgroundColor: "#000" }}></div>
                )}
                <div id="reader-hidden" style={{ display: "none" }}></div>
                <input type="file" ref={fileInputRef} hidden accept="image/*" onChange={handleImageScanFile} />

                <button onClick={() => setIsScanningCamera(!isScanningCamera)} className="btn w-100 mb-3" style={{ background: "#60a5fa", color: "#fff", fontWeight: 700, borderRadius: "12px", padding: "14px", fontSize: "16px", border: "none" }}>
                  <i className="bi bi-camera me-2"></i> {isScanningCamera ? "Stop Camera" : "Scan using Camera"}
                </button>
                <button onClick={handleImageScanClick} className="btn w-100" style={{ background: "#fff", color: "#6366f1", border: "2px solid #8b5cf6", fontWeight: 700, borderRadius: "12px", padding: "14px", fontSize: "16px" }}>
                  <i className="bi bi-image me-2"></i> Upload Image to Scan
                </button>
              </div>
            </div>

            {scannedItem && (
              <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: "16px", overflow: "hidden", borderLeft: "5px solid #22c55e !important" }}>
                <div className="card-header bg-white border-0 pt-4 pb-0">
                  <h5 className="mb-0" style={{ fontWeight: 700, color: "#1e293b" }}><i className="bi bi-check-circle-fill text-success me-2"></i> Item Details Found</h5>
                </div>
                <div className="card-body p-4">
                  <div className="row g-4 mb-4">
                    <div className="col-md-4">
                      <div className="p-3 rounded" style={{ backgroundColor: "#f1f5f9" }}>
                        <div style={{ fontSize: "12px", color: "#64748b", fontWeight: 600, textTransform: "uppercase" }}>Item Name</div>
                        <div style={{ fontSize: "16px", fontWeight: 700, color: "#0f172a" }}>{scannedItem.itemName}</div>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="p-3 rounded" style={{ backgroundColor: "#f1f5f9" }}>
                        <div style={{ fontSize: "12px", color: "#64748b", fontWeight: 600, textTransform: "uppercase" }}>Total Quantity</div>
                        <div style={{ fontSize: "16px", fontWeight: 700, color: "#0f172a" }}>{scannedItem.totalQty}</div>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="p-3 rounded" style={{ backgroundColor: "#f1f5f9" }}>
                        <div style={{ fontSize: "12px", color: "#64748b", fontWeight: 600, textTransform: "uppercase" }}>Size</div>
                        <div style={{ fontSize: "16px", fontWeight: 700, color: "#0f172a" }}>{scannedItem.size || 'N/A'}</div>
                      </div>
                    </div>
                  </div>

                  {/* 
                  <hr style={{ borderColor: "#e2e8f0", margin: "24px 0" }} />

                  <div>
                    <label className="form-label" style={{ fontWeight: 600, color: "#475569" }}>Capture / Upload Image</label>
                    <input
                      type="file"
                      id="image-upload"
                      accept="image/*"
                      capture="environment"
                      onChange={handleImageChange}
                      className="form-control form-control-lg mb-3"
                      style={{ borderRadius: "10px", border: "2px dashed #cbd5e1" }}
                    />
                    
                    {imageFile && (
                      <div className="mb-4 text-center p-3 rounded" style={{ backgroundColor: "#f8fafc", border: "1px solid #e2e8f0" }}>
                        <div className="text-start mb-2" style={{ fontSize: "13px", fontWeight: 600, color: "#64748b" }}>Image Preview:</div>
                        <img src={URL.createObjectURL(imageFile)} alt="Preview" className="img-fluid rounded shadow-sm" style={{ maxHeight: "250px", objectFit: "contain" }} />
                      </div>
                    )}

                    <button
                      onClick={handleUpload}
                      disabled={isUploading || !imageFile}
                      className="btn btn-success btn-lg w-100 shadow-sm"
                      style={{ borderRadius: "10px", fontWeight: 700, padding: "14px" }}
                    >
                      {isUploading ? <><i className="spinner-border spinner-border-sm me-2"></i> Uploading Data...</> : <><i className="bi bi-cloud-arrow-up-fill me-2"></i> Upload Data</>}
                    </button>
                  </div>
                  */}
                </div>
              </div>
            )}
          </div>

          {/* Footer removed per user request */}
        </main>
      </div>
    </div>
  );
}

export default BarcodeScanner;
